import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

class StorageService {
  get isNative() {
    return Capacitor.isNativePlatform();
  }

  /**
   * @param {string} key 
   * @param {string|object} value 
   */
  async setItem(key, value) {
    try {
      const stringValue =
        typeof value === "object" ? JSON.stringify(value) : String(value);

      // Untuk native, simpan ke Preferences
      if (this.isNative) {
        await Preferences.set({
          key: key,
          value: stringValue,
        });
      }
      // Untuk web, tidak perlu karena sudah pakai localStorage langsung
    } catch (error) {
      console.error(`Failed to set ${key} to native storage:`, error);
    }
  }

  /**
   * Get item dari storage (native only)
   * @param {string} key - Storage key
   * @returns {Promise<string|null>}
   */
  async getItem(key) {
    try {
      if (this.isNative) {
        const { value } = await Preferences.get({ key });
        return value;
      } else {
        return localStorage.getItem(key);
      }
    } catch (error) {
      console.error(`Failed to get ${key} from native storage:`, error);
      return null;
    }
  }

  /**
   * Get item dan parse sebagai JSON
   * @param {string} key 
   * @returns {Promise<object|null>}
   */
  async getItemJSON(key) {
    try {
      const value = await this.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error(`Failed to parse JSON for ${key}:`, error);
      return null;
    }
  }

  /**
   * Remove item dari storage (native only)
   * @param {string} key 
   */
  async removeItem(key) {
    try {
      if (this.isNative) {
        await Preferences.remove({ key });
      }
    } catch (error) {
      console.error(`Failed to remove ${key} from native storage:`, error);
    }
  }

   
  async syncToNative() {
    if (!this.isNative) {
      return; 
    }

    try {
      // Daftar keys yang perlu di-sync
      const keysToSync = [
        "token",
        "auth_token",
        "user",
        "user_data",
        "username",
        "email",
        "id",
        "corporate_id",
        "merchant_id",
        "branch_id",
        "member",
        "level",
        "tierId",
        "selected_business_profile",
        "tenant_domain",
        "profile_name",
        "type",
        "baseDomain",
      ];

      for (const key of keysToSync) {
        const value = localStorage.getItem(key);
        if (value) {
          await Preferences.set({ key, value });
        }
      }
    } catch (error) {
      console.error("Native Storage: Failed to sync:", error);
      throw error; 
    }
  }

  /**
   * Restore dari native storage ke localStorage
   * Dipanggil saat app start untuk auto-login
   * @returns {Promise<boolean>} 
   */
  async restoreFromNative() {
    if (!this.isNative) {
      return false; 
    }

    try {
      const { value: token } = await Preferences.get({ key: "token" });

      if (!token) {
        return false; 
      }

      const keysToRestore = [
        "token",
        "auth_token",
        "user",
        "user_data",
        "username",
        "email",
        "id",
        "corporate_id",
        "merchant_id",
        "branch_id",
        "member",
        "level",
        "tierId",
        "selected_business_profile",
        "tenant_domain",
        "profile_name",
        "type",
        "baseDomain",
      ];

      for (const key of keysToRestore) {
        const { value } = await Preferences.get({ key });
        if (value) {
          localStorage.setItem(key, value);
        }
      }

      return true;
    } catch (error) {
      console.error("Native Storage: Failed to restore:", error);
      return false;
    }
  }

  
  async clearAuthData() {
    if (!this.isNative) {
      return;
    }

    try {
      const authKeys = [
        "token",
        "auth_token",
        "user",
        "user_data",
        "username",
        "email",
        "id",
        "corporate_id",
        "merchant_id",
        "branch_id",
        "member",
        "level",
        "tierId",
        "selected_business_profile",
        "sso_ref_param",
        "campaign_referral_code",
        "autoLoginData",
        "profile_name",
        "type",
        "baseDomain",
        "tenant_domain",
      ];

      for (const key of authKeys) {
        await Preferences.remove({ key });
      }
    } catch (error) {
      console.error("Failed to clear native storage:", error);
    }
  }

  /**
   * 
   * @returns {Promise<boolean>}
   */
  async isLoggedIn() {
    const token = await this.getItem("token");
    return !!token;
  }
}

export default new StorageService();
